import { API, Storage, graphqlOperation } from 'aws-amplify';

import * as mutations from '../../graphql/mutations';
import * as queries from '../../graphql/queries';
import { createUUID } from '../../utils/createUUID';
import { MANUAL_STORAGE_LEVEL, MANUAL_STORAGE_PREFIX } from '../../utils/manualStorage';

export { getManualUrl } from '../../utils/manualStorage';

const fileExtension = name => (name.split('.').pop() || '').toUpperCase();

// Uploads the manual PDF and returns its storage key, or null on failure.
const uploadManualFile = async (file, context) => {
  const storageKey = `${MANUAL_STORAGE_PREFIX}/${createUUID()}-${file.name}`;
  try {
    await Storage.put(storageKey, file, {
      level: MANUAL_STORAGE_LEVEL,
      contentType: file.type || 'application/octet-stream',
    });
    return storageKey;
  } catch (e) {
    console.error(`${context} S3 error`, e);
    return null;
  }
};

const removeManualFile = async (storageKey, context) => {
  try {
    await Storage.remove(storageKey, { level: MANUAL_STORAGE_LEVEL });
  } catch (e) {
    console.error(`${context} S3 cleanup error`, e);
  }
};

const manualFileFields = (file, storageKey, fileSize) => ({
  documentName: storageKey,
  originalFileName: file.name,
  fileType: fileExtension(file.name),
  fileSize: Math.round(fileSize),
});

export const fetchManuals = async () => {
  try {
    const response = await API.graphql(graphqlOperation(queries.listManuals, { limit: 1000 }));
    return response.data?.listManuals?.items ?? [];
  } catch (e) {
    console.error('fetchManuals error', e);
    return [];
  }
};

export const createManualWithFile = async ({ portal, title, file, fileSize, uploadedByEmail, uploadedByFullName }) => {
  const storageKey = await uploadManualFile(file, 'createManualWithFile');
  if (!storageKey) return null;

  try {
    const response = await API.graphql(
      graphqlOperation(mutations.createManual, {
        input: {
          portal,
          title,
          ...manualFileFields(file, storageKey, fileSize),
          active: true,
          uploadedByEmail,
          uploadedByFullName,
        },
      }),
    );
    return response.data?.createManual ?? null;
  } catch (e) {
    console.error('createManualWithFile GraphQL error', e);
    // S3 upload succeeded but record creation failed — clean up the orphaned file
    await removeManualFile(storageKey, 'createManualWithFile');
    return null;
  }
};

export const updateManualRecord = async ({
  id,
  title,
  file,
  fileSize,
  uploadedByEmail,
  uploadedByFullName,
  currentDocumentName,
}) => {
  const input = { id, title };
  let newStorageKey;

  if (file) {
    newStorageKey = await uploadManualFile(file, 'updateManualRecord');
    if (!newStorageKey) return null;
    Object.assign(input, manualFileFields(file, newStorageKey, fileSize), {
      uploadedByEmail,
      uploadedByFullName,
    });
  }

  try {
    const response = await API.graphql(graphqlOperation(mutations.updateManual, { input }));
    // Replaced the file — remove the old object
    if (newStorageKey && currentDocumentName && currentDocumentName !== newStorageKey) {
      await removeManualFile(currentDocumentName, 'updateManualRecord old-file');
    }
    return response.data?.updateManual ?? null;
  } catch (e) {
    console.error('updateManualRecord GraphQL error', e);
    if (newStorageKey) {
      await removeManualFile(newStorageKey, 'updateManualRecord');
    }
    return null;
  }
};
